import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setMobileCatOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";
  const guides = [
    { name: "Complete Beginner Guide", slug: "allchinabuy-sheet-complete-guide" },
    { name: "Shipping & Delivery Guide", slug: "allchinabuy-spreadsheet-shipping-guide" },
    { name: "QC Inspection Guide", slug: "allchinabuy-qc-inspection-tips" },
    { name: "Sneaker Spreadsheet Guide", slug: "allchinabuy-sneaker-spreadsheet-guide" },
    { name: "Agent Comparison Guide", slug: "allchinabuy-spreadsheet-vs-agents" },
    { name: "Budget Finds Guide", slug: "allchinabuy-budget-finds-guide" },
    { name: "Beginners Quick Start", slug: "allchinabuy-spreadsheet-for-beginners" },
    { name: "Clothing Shopping Tips", slug: "allchinabuy-clothing-spreadsheet-tips" },
    { name: "Accessories Buying Guide", slug: "allchinabuy-accessories-guide" },
    { name: "Safety & Trust Tips", slug: "allchinabuy-spreadsheet-safety-tips" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? "bg-background-50 border-b border-background-300/60 shadow-sm"
          : "bg-background-50/70 backdrop-blur-sm border-b border-background-200/30"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-[70px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
            <div className="w-9 h-9 rounded-xl bg-primary-500 flex items-center justify-center">
              <i className="ri-grid-fill text-background-50 text-lg w-5 h-5 flex items-center justify-center"></i>
            </div>
            <span className="font-heading text-lg font-bold text-foreground-950 whitespace-nowrap">
              allchinabuy<span className="text-foreground-500 font-normal">.run</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className={`text-sm font-semibold transition-colors whitespace-nowrap uppercase tracking-wider ${isHome ? "text-primary-500" : "text-foreground-800 hover:text-primary-500"}`}>
              Home
            </Link>

            {/* Shopping Guides Dropdown */}
            <div className="relative" ref={guideRef}>
              <button
                onClick={() => { setGuideDropdownOpen(!guideDropdownOpen); setDropdownOpen(false); }}
                className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap uppercase tracking-wider cursor-pointer"
              >
                Shopping Guides
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${guideDropdownOpen ? "rotate-180" : ""} w-4 h-4 flex items-center justify-center`}></i>
              </button>

              {guideDropdownOpen && (
                <div className="absolute left-0 mt-2 w-64 bg-background-50 border border-background-200/80 rounded-xl py-2 shadow-lg max-h-[380px] overflow-y-auto">
                  {guides.map((g) => (
                    <Link
                      key={g.slug}
                      to={`/guide/${g.slug}`}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      onClick={() => setGuideDropdownOpen(false)}
                    >
                      {g.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/#categories" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap uppercase tracking-wider">Categories</Link>
            <Link to="/#faq" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap uppercase tracking-wider">FAQ</Link>
            <Link to="/#safety" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap uppercase tracking-wider">Safety</Link>

            {/* Category Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => { setDropdownOpen(!dropdownOpen); setGuideDropdownOpen(false); }}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-foreground-300/50 bg-background-50 text-sm font-semibold text-foreground-800 hover:border-primary-500 hover:text-primary-500 transition-all whitespace-nowrap cursor-pointer"
              >
                <span>Jump to Category</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""} w-4 h-4 flex items-center justify-center`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-background-50 border border-background-200/80 rounded-xl py-2 shadow-lg max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-foreground-950 w-5 h-5 flex items-center justify-center`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-5 py-5 flex flex-col gap-3 max-h-[80vh] overflow-y-auto">
          {/* Primary Nav */}
          <Link to="/" onClick={() => setMobileOpen(false)} className={`text-sm font-semibold py-2.5 px-3 rounded-lg uppercase tracking-wider ${isHome ? "bg-primary-500 text-background-50" : "text-foreground-800 hover:bg-background-100"}`}>
            <i className="ri-home-4-line mr-2 w-4 h-4 inline-flex items-center justify-center align-middle"></i>
            Home
          </Link>

          <Link to="/#categories" onClick={() => setMobileOpen(false)} className="text-sm font-semibold text-foreground-800 py-2.5 px-3 rounded-lg hover:bg-background-100 uppercase tracking-wider">
            <i className="ri-grid-line mr-2 w-4 h-4 inline-flex items-center justify-center align-middle"></i>
            Categories
          </Link>

          {/* Mobile Shopping Guides Accordion */}
          <div>
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between text-sm font-semibold text-foreground-800 py-2.5 px-3 rounded-lg hover:bg-background-100 uppercase tracking-wider cursor-pointer"
            >
              <span>
                <i className="ri-book-open-line mr-2 w-4 h-4 inline-flex items-center justify-center align-middle"></i>
                Shopping Guides
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""} w-5 h-5 flex items-center justify-center`}></i>
            </button>
            {mobileCatOpen && (
              <div className="ml-4 mt-1 flex flex-col gap-1 border-l-2 border-background-200 pl-4">
                {guides.map((g) => (
                  <Link
                    key={g.slug}
                    to={`/guide/${g.slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="text-sm text-foreground-600 py-2 px-2 rounded-md hover:text-foreground-950 hover:bg-background-100 transition-colors"
                  >
                    {g.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/#faq" onClick={() => setMobileOpen(false)} className="text-sm font-semibold text-foreground-800 py-2.5 px-3 rounded-lg hover:bg-background-100 uppercase tracking-wider">
            <i className="ri-question-line mr-2 w-4 h-4 inline-flex items-center justify-center align-middle"></i>
            FAQ
          </Link>

          <Link to="/#safety" onClick={() => setMobileOpen(false)} className="text-sm font-semibold text-foreground-800 py-2.5 px-3 rounded-lg hover:bg-background-100 uppercase tracking-wider">
            <i className="ri-shield-check-line mr-2 w-4 h-4 inline-flex items-center justify-center align-middle"></i>
            Safety
          </Link>

          {/* Mobile Categories Grid */}
          <div className="pt-3 border-t border-background-200/70">
            <p className="text-xs text-foreground-500 mb-3 px-3 uppercase tracking-widest font-semibold">Quick Categories</p>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.seq}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-700 py-2 px-3 rounded-lg hover:bg-background-100 hover:text-primary-500 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}